import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  useBudgetRecommenderControllerGetRowSettingsQuery,
  useBudgetRecommenderControllerUpsertRowSettingMutation,
} from "~/ui-rtk/api/budgetRecommenderApi"
import { DEFAULT_ANALYSIS_PERIOD } from "../types"

/** Per-row analysis period state: saved settings from the DB merged with
 *  session-local overrides (override wins). Saving persists the override
 *  to the DB so it becomes the load-time default. */
export function useAnalysisPeriods() {
  const { data: rowSettings } =
    useBudgetRecommenderControllerGetRowSettingsQuery()
  const [upsertRowSetting] =
    useBudgetRecommenderControllerUpsertRowSettingMutation()

  const [periodOverrides, setPeriodOverrides] = useState<Map<string, number>>(
    new Map(),
  )

  const savedPeriods = useMemo(() => {
    const map = new Map<string, number>()
    for (const s of rowSettings ?? []) {
      // A pin-only row has no saved period: the default applies.
      if (s.analysisPeriodDays !== null) {
        map.set(s.entityId, s.analysisPeriodDays)
      }
    }
    return map
  }, [rowSettings])

  // Prune overrides that match the saved/default value (e.g. after a save
  // round-trips through the invalidated query) so isDirty stays accurate
  // without flashing back to the stale saved value mid-refetch.
  useEffect(() => {
    setPeriodOverrides(prev => {
      let changed = false
      const next = new Map(prev)
      for (const [entityId, days] of prev) {
        if ((savedPeriods.get(entityId) ?? DEFAULT_ANALYSIS_PERIOD) === days) {
          next.delete(entityId)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [savedPeriods])

  const effectivePeriods = useMemo(
    () => new Map([...savedPeriods, ...periodOverrides]),
    [savedPeriods, periodOverrides],
  )

  /** Session-only change — recalculates the row without persisting */
  const setPeriod = useCallback((entityId: string, days: number) => {
    setPeriodOverrides(prev => {
      const next = new Map(prev)
      next.set(entityId, days)
      return next
    })
  }, [])

  /** Session-only bulk change: a one-time apply of `days` to every given row.
   *  Rows remain individually changeable afterwards. */
  const setAllPeriods = useCallback((entityIds: string[], days: number) => {
    setPeriodOverrides(prev => {
      const next = new Map(prev)
      for (const entityId of entityIds) {
        next.set(entityId, days)
      }
      return next
    })
  }, [])

  /** Session-only per-row bulk change: every entry sets its own row's
   *  period (auto-select by confidence). Rows remain individually
   *  changeable afterwards. */
  const setPeriods = useCallback((updates: ReadonlyMap<string, number>) => {
    setPeriodOverrides(prev => {
      const next = new Map(prev)
      for (const [entityId, days] of updates) {
        next.set(entityId, days)
      }
      return next
    })
  }, [])

  /** Persist the period so it becomes this row's load-time default */
  const savePeriod = useCallback(
    async (
      entityType: "campaign" | "adset",
      entityId: string,
      days: number,
    ) => {
      try {
        await upsertRowSetting({
          entityType,
          entityId,
          analysisPeriodDays: days,
        }).unwrap()
        toast.success("Analysis period saved")
      } catch (error) {
        console.error("Failed to save analysis period:", error)
        toast.error("Failed to save analysis period")
      }
    },
    [upsertRowSetting],
  )

  /** true when the session override differs from the saved (or default) value */
  const isDirty = useCallback(
    (entityId: string) => {
      const override = periodOverrides.get(entityId)
      if (override === undefined) return false
      return (
        override !== (savedPeriods.get(entityId) ?? DEFAULT_ANALYSIS_PERIOD)
      )
    },
    [periodOverrides, savedPeriods],
  )

  return {
    effectivePeriods,
    setPeriod,
    setAllPeriods,
    setPeriods,
    savePeriod,
    isDirty,
  }
}
